import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../common/Toast'
import { authAPI, dashboardAPI } from '../../services/api'
import { decodeHtmlEntities } from '../../utils/displayText'
import { unlockAlertAudio, playTenMinuteChime, playOneMinuteChime } from '../../utils/meetingAlertSounds'

const REMINDER_MINUTES = 15
/** Audio-only alerts: a calm chime at T-10, an urgent triple beep at T-1. */
const AUDIO_ALERT_MINUTES = [10, 1]
const MAX_SCHEDULE_AHEAD_MS = 24 * 60 * 60 * 1000
const STORAGE_KEY_PREFIX = 'rithmo_reminded_'
const LOG_PREFIX = '[MeetingReminder]'
/** Re-fetch every 10 minutes so recurring meeting times stay fresh */
const REFETCH_INTERVAL_MS = 10 * 60 * 1000
/** Stop visual alerts 20 min after T-15 reminder fires (= 5 min after meeting starts) */
const FLASH_CLEANUP_MS = 20 * 60 * 1000
const STORAGE_START_KEY_PREFIX = 'rithmo_start_alerted_'

function alreadyStartAlerted(meetingId) {
  try { return sessionStorage.getItem(STORAGE_START_KEY_PREFIX + meetingId) === '1' } catch { return false }
}
function markStartAlerted(meetingId) {
  try { sessionStorage.setItem(STORAGE_START_KEY_PREFIX + meetingId, '1') } catch { /* ignore */ }
}

/** Per-meeting, per-tier guard so each audio chime fires at most once per session. */
const STORAGE_AUDIO_KEY_PREFIX = 'rithmo_audio_alerted_'
function alreadyAudioAlerted(meetingId, minutes) {
  try { return sessionStorage.getItem(`${STORAGE_AUDIO_KEY_PREFIX}${minutes}_${meetingId}`) === '1' } catch { return false }
}
function markAudioAlerted(meetingId, minutes) {
  try { sessionStorage.setItem(`${STORAGE_AUDIO_KEY_PREFIX}${minutes}_${meetingId}`, '1') } catch { /* ignore */ }
}

/** Lazily render a red-circle alert favicon via canvas; cached after first call. */
let _alertFaviconUrl = null
function getAlertFaviconUrl() {
  if (_alertFaviconUrl) return _alertFaviconUrl
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 32
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#EF4444'
    ctx.beginPath()
    ctx.arc(16, 16, 15, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 20px system-ui, -apple-system, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('!', 16, 17)
    _alertFaviconUrl = canvas.toDataURL('image/png')
  } catch {
    _alertFaviconUrl = null
  }
  return _alertFaviconUrl
}

function alreadyNotified(meetingId) {
  if (typeof sessionStorage === 'undefined') return false
  return sessionStorage.getItem(STORAGE_KEY_PREFIX + meetingId) === '1'
}

function markNotified(meetingId) {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(STORAGE_KEY_PREFIX + meetingId, '1')
}

function formatReminderTime(startTime) {
  if (!startTime) return ''
  const d = new Date(startTime)
  if (Number.isNaN(d.getTime())) return ''
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h = hours % 12 || 12
  const m = minutes < 10 ? '0' + minutes : minutes
  return `${h}:${m} ${ampm}`
}

export default function MeetingReminder() {
  const { user } = useAuth()
  const toast = useToast()
  const notifiedRef = useRef(new Set())
  const permissionRequestedRef = useRef(false)
  const timeoutsRef = useRef([])
  const swRef = useRef(null)
  const originalTitleRef = useRef(null)
  const originalFaviconHrefRef = useRef(null)
  const flashIntervalRef = useRef(null)
  const flashCleanupRef = useRef(null)

  /** Swap favicon to red alert icon and start tab-title flashing. Idempotent. */
  const startVisualAlerts = useCallback(() => {
    // Favicon swap
    const link = document.querySelector("link[rel~='icon']")
    if (link) {
      if (!originalFaviconHrefRef.current) originalFaviconHrefRef.current = link.getAttribute('href')
      const url = getAlertFaviconUrl()
      if (url) link.href = url
    }
    // Tab title flash
    if (!originalTitleRef.current) originalTitleRef.current = document.title
    if (!flashIntervalRef.current) {
      let toggled = false
      flashIntervalRef.current = setInterval(() => {
        document.title = toggled ? (originalTitleRef.current || 'Rithmo') : '⚠️ Meeting Starting — Rithmo'
        toggled = !toggled
      }, 1200)
    }
    // Auto-cleanup so the tab doesn't flash indefinitely
    if (flashCleanupRef.current) clearTimeout(flashCleanupRef.current)
    flashCleanupRef.current = setTimeout(() => stopVisualAlerts(), FLASH_CLEANUP_MS) // eslint-disable-line no-use-before-define
  }, [])

  /** Restore original tab title and favicon; clear flash interval. */
  const stopVisualAlerts = useCallback(() => {
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current)
      flashIntervalRef.current = null
    }
    document.title = originalTitleRef.current || document.title
    originalTitleRef.current = null
    const link = document.querySelector("link[rel~='icon']")
    if (link && originalFaviconHrefRef.current) link.href = originalFaviconHrefRef.current
    originalFaviconHrefRef.current = null
    if (flashCleanupRef.current) {
      clearTimeout(flashCleanupRef.current)
      flashCleanupRef.current = null
    }
  }, [])

  /** Play the tier-specific audio chime once for this meeting. */
  const playAudioAlert = useCallback((meeting, minutes) => {
    const meetingId = meeting.id ?? meeting.meetingId
    if (alreadyAudioAlerted(meetingId, minutes)) return
    markAudioAlerted(meetingId, minutes)
    if (minutes === 1) playOneMinuteChime()
    else playTenMinuteChime()
  }, [])

  /** Fire the custom event that MeetingAlertOverlay listens for. */
  const dispatchStartAlert = useCallback((meeting) => {
    markStartAlerted(meeting.id ?? meeting.meetingId)
    window.dispatchEvent(new CustomEvent('rithmo:meeting-start-alert', { detail: meeting }))
  }, [])

  const requestDesktopPermission = useCallback(() => {
    if (permissionRequestedRef.current || typeof Notification === 'undefined') return
    permissionRequestedRef.current = true
    if (Notification.permission === 'default') {
      Notification.requestPermission().then((result) => {
        console.debug(LOG_PREFIX, 'Notification permission result', { result })
      }).catch((err) => {
        console.warn(LOG_PREFIX, 'Notification permission request failed', { error: String(err?.message ?? err) })
      })
    }
  }, [])

  const showReminder = useCallback(
    (meeting) => {
      const meetingId = meeting.id ?? meeting.meetingId
      if (notifiedRef.current.has(meetingId) || alreadyNotified(meetingId)) return
      notifiedRef.current.add(meetingId)
      markNotified(meetingId)
      startVisualAlerts()

      const title = decodeHtmlEntities(meeting.title || 'Meeting')
      const timeStr = formatReminderTime(meeting.startTime ?? meeting.scheduledStartTime)
      const bodyCopy = timeStr ? `${title} starts at ${timeStr}` : `${title} starts in ${REMINDER_MINUTES} minutes`
      const tag = `rithmo-meeting-${meetingId}`

      // Preferred path: route through service worker so the popup fires even when the
      // Rithmo tab is in the background. Chrome blocks main-thread Notification() when
      // the tab is not the active foreground window.
      const sw = swRef.current?.active ?? swRef.current?.installing ?? swRef.current?.waiting
      if (sw) {
        sw.postMessage({ type: 'SHOW_NOTIFICATION', title: 'Meeting starting soon', body: bodyCopy, tag })
      } else if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // Fallback: main-thread notification — only reliable when the tab is focused.
        try {
          const n = new Notification('Meeting starting soon', {
            body: bodyCopy,
            icon: '/favicon.ico',
            tag,
          })
          n.onclick = () => {
            window.focus()
            n.close()
          }
        } catch (e) {
          console.warn(LOG_PREFIX, 'Desktop notification failed', { meetingId, error: String(e?.message ?? e) })
        }
      }

      toast.info('Meeting in 15 min', bodyCopy)
    },
    [toast, startVisualAlerts]
  )

  const scheduleReminders = useCallback(
    (prefs, upcoming) => {
      timeoutsRef.current.forEach((id) => clearTimeout(id))
      timeoutsRef.current = []

      if (!prefs || prefs.meetingReminders === false) {
        if (!prefs) {
          console.debug(LOG_PREFIX, 'Skipped: no notification prefs from API')
        } else if (prefs.meetingReminders === false) {
          console.debug(LOG_PREFIX, 'Skipped: meeting reminders disabled in settings')
        }
        return
      }
      if (!upcoming.length) {
        console.debug(LOG_PREFIX, 'Skipped: no upcoming meetings')
        return
      }
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        console.debug(LOG_PREFIX, 'Desktop notifications blocked by browser; toast-only reminders')
      }

      requestDesktopPermission()

      const now = Date.now()
      const reminderOffsetMs = REMINDER_MINUTES * 60 * 1000
      const scheduledIds = []

      for (const meeting of upcoming) {
        const startStr = meeting.startTime ?? meeting.scheduledStartTime
        if (!startStr) continue
        const startMs = new Date(startStr).getTime()
        if (Number.isNaN(startMs)) continue
        const meetingId = meeting.id ?? meeting.meetingId

        const reminderDelay = (startMs - reminderOffsetMs) - now

        // T-15 reminder: OS notification + toast + visual alerts
        if (reminderDelay <= 0) {
          if (now <= startMs) showReminder(meeting)
        } else if (reminderDelay <= MAX_SCHEDULE_AHEAD_MS && !alreadyNotified(meetingId)) {
          const id = setTimeout(() => showReminder(meeting), reminderDelay)
          timeoutsRef.current.push(id)
          scheduledIds.push(meetingId)
        }

        // Audio-only alerts at T-10 and T-1 (distinct sounds). Independent of
        // the T-15 visual/notification reminder above.
        for (const minutes of AUDIO_ALERT_MINUTES) {
          if (alreadyAudioAlerted(meetingId, minutes)) continue
          const audioDelay = (startMs - minutes * 60 * 1000) - now
          if (audioDelay <= 0) {
            // Window already passed its mark — only fire if the meeting hasn't started yet.
            if (now < startMs) playAudioAlert(meeting, minutes)
          } else if (audioDelay <= MAX_SCHEDULE_AHEAD_MS) {
            const id = setTimeout(() => playAudioAlert(meeting, minutes), audioDelay)
            timeoutsRef.current.push(id)
          }
        }

        // T-0 start alert: fires the in-app overlay via custom event
        const startDelay = startMs - now
        if (!alreadyStartAlerted(meetingId)) {
          if (startDelay <= 0) {
            // Meeting already started — show if still within the 15-min window
            if (now < startMs + reminderOffsetMs) dispatchStartAlert(meeting)
          } else if (startDelay <= MAX_SCHEDULE_AHEAD_MS) {
            const id = setTimeout(() => dispatchStartAlert(meeting), startDelay)
            timeoutsRef.current.push(id)
          }
        }
      }
      if (scheduledIds.length > 0) {
        console.debug(LOG_PREFIX, 'Scheduled reminders', { count: scheduledIds.length, meetingIds: scheduledIds })
      }
    },
    [requestDesktopPermission, showReminder, dispatchStartAlert, playAudioAlert]
  )

  const fetchAndSchedule = useCallback(async () => {
    try {
      // Fetch notification prefs and dashboard data in parallel.
      // Also fetch today's calendar meetings so recurring events (which have a stale
      // scheduled_start_time on the parent row) are included with their real occurrence time.
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)
      const todayEnd = new Date()
      todayEnd.setHours(23, 59, 59, 999)

      const [notifRes, dashboardRes, calendarRes] = await Promise.all([
        authAPI.getNotifications(),
        dashboardAPI.get(),
        dashboardAPI.getCalendarMeetings(
          todayStart.toISOString(),
          todayEnd.toISOString()
        ).catch(() => null),
      ])

      const prefs = notifRes?.data?.notifications ?? notifRes?.notifications
      const payload = dashboardRes?.data ?? dashboardRes

      // Start with dashboard upcoming meetings
      const dashboardUpcoming = payload?.upcomingMeetings ?? []

      // Merge calendar meetings for today — these include recurring occurrences with
      // the correct start_at time, which the dashboard parent row does not have.
      const calendarMeetings = calendarRes?.data?.meetings ?? calendarRes?.meetings ?? []
      const now = Date.now()

      // Build a set of meeting IDs already in dashboardUpcoming so we don't double-schedule
      const dashboardIds = new Set(dashboardUpcoming.map((m) => m.id ?? m.meetingId))

      // From calendar: only include meetings that start within the next 24h and aren't
      // already in the dashboard list
      const calendarOnly = calendarMeetings.filter((m) => {
        const id = m.id ?? m.meetingId
        if (dashboardIds.has(id)) return false
        const startStr = m.startTime ?? m.scheduledStartTime
        if (!startStr) return false
        const startMs = new Date(startStr).getTime()
        return !Number.isNaN(startMs) && startMs > now && startMs <= now + MAX_SCHEDULE_AHEAD_MS
      })

      // For recurring parent meetings in the dashboard list, their startTime is the original
      // creation date (past). Replace it with today's calendar occurrence time if available.
      const calendarBySeriesId = {}
      const calendarByProviderMeetingId = {}
      for (const cm of calendarMeetings) {
        const startStr = cm.startTime ?? cm.scheduledStartTime
        if (!startStr) continue
        const startMs = new Date(startStr).getTime()
        if (Number.isNaN(startMs) || startMs <= now) continue
        if (cm.recurringSeriesId) calendarBySeriesId[cm.recurringSeriesId] = cm
        if (cm.id) calendarByProviderMeetingId[cm.id] = cm
      }

      const upcoming = [
        ...dashboardUpcoming.map((m) => {
          if (!m.isRecurring) return m
          // Recurring parent: find the next occurrence from calendar data
          const calMatch =
            (m.recurringSeriesId && calendarBySeriesId[m.recurringSeriesId]) ||
            calendarByProviderMeetingId[m.id ?? m.meetingId]
          if (!calMatch) return m
          const occurrenceStartStr = calMatch.startTime ?? calMatch.scheduledStartTime
          if (!occurrenceStartStr) return m
          // Return a copy with the real occurrence start time
          return { ...m, startTime: occurrenceStartStr, scheduledStartTime: occurrenceStartStr }
        }),
        ...calendarOnly,
      ]

      scheduleReminders(prefs, upcoming)
    } catch (e) {
      console.warn(LOG_PREFIX, 'fetchAndSchedule failed', { error: String(e?.message ?? e) })
    }
  }, [scheduleReminders])

  useEffect(() => {
    // Browsers block audio until a user gesture. Unlock the AudioContext on the
    // first interaction so scheduled chimes can play later.
    const unlock = () => unlockAlertAudio()
    window.addEventListener('pointerdown', unlock, { once: true })
    window.addEventListener('keydown', unlock, { once: true })
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      swRef.current = reg
    }).catch((err) => {
      console.warn(LOG_PREFIX, 'Service worker registration failed', { error: String(err?.message ?? err) })
    })
  }, [])

  useEffect(() => {
    if (!user) return

    fetchAndSchedule()

    const onFocus = () => fetchAndSchedule()
    window.addEventListener('focus', onFocus)

    const onDismiss = () => stopVisualAlerts()
    window.addEventListener('rithmo:meeting-alert-dismissed', onDismiss)

    // Periodically re-fetch so recurring meeting times stay fresh and new meetings
    // scheduled during the session are picked up without requiring a page reload.
    const intervalId = setInterval(() => {
      fetchAndSchedule()
    }, REFETCH_INTERVAL_MS)

    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('rithmo:meeting-alert-dismissed', onDismiss)
      clearInterval(intervalId)
      timeoutsRef.current.forEach((id) => clearTimeout(id))
      timeoutsRef.current = []
      stopVisualAlerts()
    }
  }, [user, fetchAndSchedule, stopVisualAlerts])

  return null
}
